import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

/**
 *
 * Generic edit dialog component
 * Used to edit tags, terms, description, owners, and custom metadata of any asset
 *
 */
import {
  AssetService,
  OwnerService,
  TagDto,
  type AssetListDto,
  type AssetPatchDto,
  type EntityCustomMetadataDto,
  type UserDto,
} from '@/api';
import ModalDialog from '@/components/modal-dialog/Modal-Dialog.vue';
import { Inputs } from '@/@types/types';
import { createPatchDto, createCustomMetadataPatchDto } from '@/api/patches';
import { type GenericEditDialogFields } from '@/components/generic-edit-dialog/GenericEditDialog';
import { AssetsTypesEnum } from '@/components/custom-metadata/CustomMetadata';
import i18n from '@/i18n';
import SRichTextEditor from '@/components/SRichTextEditor.vue';
import CustomMetadataSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataSelector.vue';
import Constants from '@/utils/constants';

interface EditDialogProps {
  fields?: GenericEditDialogFields[];
  customTitle?: string;
  saveAndAddMode?: boolean;
  hasRichText?: boolean;
  shouldFetchDetails?: boolean;
}

type EditableEntity = {
  name?: string;
  urn?: string;
  tags?: TagDto[];
  terms?: TagDto[];
  owners?: UserDto[];
  description?: string;
  customMetadata?: EntityCustomMetadataDto[];
};

type AssetsEditEmits = {
  (event: 'update', data: { entities: any, patches: any }): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GenericEditDialog',
  props: {
  fields: { default: () => [] },
  customTitle: { default: '' },
  saveAndAddMode: { type: Boolean, default: false },
  hasRichText: { type: Boolean, default: false },
  shouldFetchDetails: { type: Boolean, default: false }
},
  emits: ["update"],
  setup(__props, { expose, emit }) {

const props = __props;




const modalDialogRef = _ref<InstanceType<typeof ModalDialog> | null>(null);
const counter = _ref(Inputs.INPUT_HIGH_CHARS);
const richTextEditor = _ref<InstanceType<typeof SRichTextEditor> | null>(null);

let tags = _ref<TagDto[] | undefined>([]);
let terms = _ref<TagDto[] | undefined>([]);
let owners = _ref<UserDto[] | undefined>([]);
let possibleOwners = _ref<UserDto[]>([]);
let description: EditableEntity['description'] = _ref('');
let entityStates = _ref<Record<string, EditableEntity>>({});
let customMetadata = _ref<EntityCustomMetadataDto[]>([]);
let urn = _ref('');
let name = _ref('');
let entities = _ref<EditableEntity['urn'][]>([]);
let removeExistingTags = _ref(false);
let removeExistingTerms = _ref(false);
let removeExistingOwners = _ref(false);
let assetType = _ref<AssetsTypesEnum>(AssetsTypesEnum.DASHBOARD);

const isSingleEdit = _computed(() => entities.value.length === 1);
const entitiesCount = _computed<number>(() => entities.value.length);
const hasTags = _computed(() => props.fields?.includes('tags') || false);
const hasTerms = _computed(() => props.fields?.includes('terms') || false);
const hasDescription = _computed(() => props.fields?.includes('description') || false);
const hasOwners = _computed(() => props.fields?.includes('owners') || false);
const hasCustomMetadata = _computed(() => props.fields?.includes('custom-metadata') || false);
const saveButtonLabel = _computed(() => (props.saveAndAddMode ? 'edit-dialog.save_and_add' : 'edit-dialog.save'));
const shouldFetchOverview = _computed(() => props.shouldFetchDetails && props.hasRichText);
const charLimit = _computed(() => (props.hasRichText ? Constants.RICH_TEXT_CHAR_LIMIT : Constants.NORMAL_TEXT_CHAR_LIMIT));
const descriptionRules = _computed(() => [(v: string) => v?.length <= charLimit.value || `${charLimit.value} chars max`]);
const hasReachedLimit = _computed(() => description.value?.length! > charLimit.value);
const descriptionChars = _computed<string>(() => {
  if (description.value && description.value.length.toLocaleString()) {
    return `${description.value.length.toLocaleString()} / ${charLimit.value.toLocaleString()}`;
  }
  return `0 / ${charLimit.value.toLocaleString()}`;
});

const title = _computed<string>(() => {
  if (props.customTitle) {
    return props.customTitle;
  }
  return isSingleEdit.value ? `${i18n.t('edit-dialog.edit')} ${name.value}` : i18n.tc('edit-dialog.multiple_datasets', entitiesCount.value) as string;
});

const getPatch = (currentState: EditableEntity) => {
  let tagIds: any = [];
  let termIds: any = [];
  let ownerIds: any = [];
  let newDescription: string | undefined = '';
  let customMetadataEntryIds: string[] = [];

  props.fields.forEach((field) => {
    switch (field) {
      case 'tags':
        tagIds = createPatchDto({ tags: tags.value }, currentState.tags, removeExistingTags.value, isSingleEdit.value);
        break;
      case 'terms':
        termIds = createPatchDto({ terms: terms.value }, currentState.terms, removeExistingTerms.value, isSingleEdit.value);
        break;
      case 'owners':
        ownerIds = createPatchDto({ owners: owners.value }, currentState.owners, removeExistingOwners.value, isSingleEdit.value);
        break;
      case 'description':
        if (!isSingleEdit.value && !description.value) {
          // If the description is empty, we want to keep the current description
          newDescription = undefined;
        } else {
          newDescription = description.value;
        }
        break;
      case 'custom-metadata':
        customMetadataEntryIds = createCustomMetadataPatchDto(customMetadata.value, currentState.customMetadata);
        break;
      default:
        break;
    }
  });

  const patch: {
    tagIds: string[];
    termIds: string[];
    description?: string;
    ownerIds: string[];
    customMetadataEntryIds?: string[];
  } = {
    tagIds,
    termIds,
    description: newDescription,
    ownerIds,
  };

  // Always include customMetadataEntryIds if custom-metadata is in the fields array
  if (props.fields.includes('custom-metadata')) {
    patch.customMetadataEntryIds = customMetadataEntryIds;
  }

  return patch;
};

const init = () => {
  entities.value = [];
  tags.value = [];
  terms.value = [];
  description.value = '';
  owners.value = [];
  possibleOwners.value = [];
  customMetadata.value = [];
  removeExistingTags.value = false;
  removeExistingTerms.value = false;
  removeExistingOwners.value = false;
  entityStates.value = {};
};

const open = () => modalDialogRef.value!.openDialog();
const close = () => modalDialogRef.value!.closeDialog();

const update = async () => {
  const patches: AssetPatchDto[] = [];
  entities.value.forEach((entity: any) => patches.push(getPatch(entityStates.value[entity])));
  emit('update', { entities: entities.value, patches });
  close();
};

const fetchPossibleOwners = async (assetUrns: string[]) => {
  const requestBody: AssetListDto = {
    assetIds: assetUrns.map((assetUrn) => assetUrn.split(':')[1]),
  };
  const { data } = await OwnerService.getAllPossibleOwnersUi({ requestBody });
  possibleOwners.value = data!;
};

/**
 * Set the entities to be edited
 * Publicly exposed method.
 * @param newEntities
 * @param type
 */
const setEntities = async (newEntities: EditableEntity[], type: AssetsTypesEnum = AssetsTypesEnum.DASHBOARD) => {
  init();
  assetType.value = type;
  entities.value = newEntities.map((entity) => entity.urn);

  if (newEntities.length === 1) {
    const entity = newEntities[0];

    if (shouldFetchOverview.value) {
      const response = await AssetService.getAssetOverviewByUrn({ urn: entity.urn! });
      richTextEditor.value?.setHTMLValue(response.details?.description!);
      customMetadata.value = response.details?.customMetadata || [];
    }

    urn.value = entity.urn!;
    name.value = entity.name || '';
    if (entity.tags) tags.value = entity.tags;
    if (entity.terms) terms.value = entity.terms;
    if (entity.owners) owners.value = entity.owners;
    if (entity.description && !shouldFetchOverview.value) description.value = entity.description;
    if (entity.customMetadata) customMetadata.value = entity.customMetadata;
  } else if (newEntities.length > 1 && shouldFetchOverview.value) {
    const response = await AssetService.getAssetOverviewByUrn({ urn: newEntities[0].urn! });
    // Preserve custom metadata structure but empty all entries
    customMetadata.value = response.details?.customMetadata?.map((metadata) => ({
      ...metadata,
      entries: [],
    })) || [];
  }

  const assetUrns: string[] = [];
  newEntities.forEach((entity) => {
    const assetUrn = entity.urn || '';
    // Update the current state for each entity
    entityStates.value[assetUrn] = {
      ...entity,
      customMetadata: customMetadata.value,
      urn: urn.value,
    };
    assetUrns.push(assetUrn);
  });

  if (hasOwners.value) {
    await fetchPossibleOwners(assetUrns);
  }

  open();
};

const resetEditor = () => {
  richTextEditor.value?.setHTMLValue('');
};

const handleClose = () => {
  if (shouldFetchOverview.value) {
    resetEditor();
  }
};

expose({ setEntities });

return { __sfc: true,emit, props, modalDialogRef, counter, richTextEditor, tags, terms, owners, possibleOwners, description, entityStates, customMetadata, urn, name, entities, removeExistingTags, removeExistingTerms, removeExistingOwners, assetType, isSingleEdit, entitiesCount, hasTags, hasTerms, hasDescription, hasOwners, hasCustomMetadata, saveButtonLabel, shouldFetchOverview, charLimit, descriptionRules, hasReachedLimit, descriptionChars, title, getPatch, init, open, close, update, fetchPossibleOwners, setEntities, resetEditor, handleClose, _ref, _computed, AssetService, OwnerService, TagDto, ModalDialog, Inputs, createPatchDto, createCustomMetadataPatchDto, AssetsTypesEnum, i18n, SRichTextEditor, CustomMetadataSelector, Constants }
}

})